import React, { useState, useEffect, useRef, useCallback } from 'react';
import './DirectChat.css';
import {
  connectSocket,
  getSocket,
  joinDirectRoom,
  leaveDirectRoom,
  sendDirectMessage,
  onDirectMessageReceived,
  offDirectMessageReceived,
  onDirectMessageDeleted,
  offDirectMessageDeleted,
  onDirectTyping,
  offDirectTyping,
  emitDirectTypingStart,
  emitDirectTypingStop,
  deleteDirectMessage,
} from '../services/socketService';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const DirectChat = ({ conversation, userData, token, onBack }) => {
  const [messages, setMessages]         = useState([]);
  const [inputText, setInputText]       = useState('');
  const [typingUsers, setTypingUsers]   = useState([]);
  const [connected, setConnected]       = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sending, setSending]           = useState(false);

  const bottomRef      = useRef(null);
  const typingTimerRef = useRef(null);
  const isTypingRef    = useRef(false);
  const imageInputRef  = useRef(null);
  const fileInputRef   = useRef(null);

  const guestId  = conversation.guest_id;
  const userId   = userData?.id;
  const userName = `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || `User ${userId}`;
  const otherUser = conversation.other_user;

  // ── Replace backend host with proxy ───────────────────────────────────────
  const getProxiedUrl = (url) => {
    if (!url) return url;
    return url
      .replace('http://37.60.253.184:8081', API_BASE)
      .replace('https://37.60.253.184:8081', API_BASE);
  };

  // ── Fetch message history ─────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const res = await fetch(`${API_BASE}/api/direct/messages/${guestId}`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        const history = data.data.map(msg => ({
          id:          msg.id,
          guestId:     msg.guest_id,
          senderId:    msg.sender_id,
          senderName:  msg.sender?.name || `User ${msg.sender_id}`,
          receiverId:  msg.receiver_id,
          messageType: msg.message_type || 'text',
          message:     msg.message || '',
          fileUrl:     msg.file_url ? getProxiedUrl(msg.file_url) : null,
          fileName:    msg.file_name || null,
          isRead:      msg.is_read,
          timestamp:   msg.created_at,
        }));
        setMessages(history);
      }
    } catch (err) {
      console.error('[DirectChat] History fetch error:', err);
    } finally {
      setLoadingHistory(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestId, token]);

  // ── Attach socket listeners ───────────────────────────────────────────────
  const attachListeners = useCallback((sock) => {
    sock.off('direct:message:received');
    sock.off('direct:typing:user');
    sock.off('direct:message:deleted');

    sock.on('direct:message:received', msg => {
      if (String(msg.guestId) !== String(guestId)) return;
      setMessages(prev => {
        if (prev.some(m => String(m.id) === String(msg.id))) return prev;
        return [...prev, {
          id:          msg.id,
          guestId:     msg.guestId,
          senderId:    msg.senderId,
          senderName:  msg.senderName,
          receiverId:  msg.receiverId,
          messageType: msg.messageType || 'text',
          message:     msg.message || '',
          fileUrl:     msg.file_url ? getProxiedUrl(msg.file_url) : null,
          fileName:    msg.file_name || null,
          isRead:      msg.isRead || false,
          timestamp:   msg.timestamp,
        }];
      });
    });

    sock.on('direct:typing:user', ({ guestId: gid, userId: tuid, userName: tuname, isTyping }) => {
      if (String(gid) !== String(guestId)) return;
      if (String(tuid) === String(userId)) return;
      setTypingUsers(prev =>
        isTyping
          ? prev.includes(tuname) ? prev : [...prev, tuname]
          : prev.filter(n => n !== tuname)
      );
    });

    sock.on('direct:message:deleted', ({ messageId }) => {
      setMessages(prev => prev.filter(m => String(m.id) !== String(messageId)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestId, userId]);

  // ── Connect & join room ───────────────────────────────────────────────────
  useEffect(() => {
    fetchHistory();

    const sock = connectSocket(userId, userName);

    if (sock.connected) {
      setConnected(true);
      attachListeners(sock);
      joinDirectRoom(guestId, userId, userName);
    }

    sock.on('connect', () => {
      setConnected(true);
      attachListeners(sock);
      joinDirectRoom(guestId, userId, userName);
    });

    sock.on('disconnect', () => setConnected(false));

    return () => {
      leaveDirectRoom(guestId, userId, userName);
      sock.off('connect');
      sock.off('disconnect');
      sock.off('direct:message:received');
      sock.off('direct:typing:user');
      sock.off('direct:message:deleted');
    };
  }, [guestId, userId, userName, attachListeners, fetchHistory]);

  useEffect(() => {
    const sock = getSocket();
    if (sock && sock.connected) {
      setConnected(true);
      attachListeners(sock);
    }
  }, [attachListeners]);

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send text ─────────────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || sending) return;

    setInputText('');
    if (isTypingRef.current) {
      emitDirectTypingStop(guestId, userId, userName);
      isTypingRef.current = false;
    }
    clearTimeout(typingTimerRef.current);
    setSending(true);

    try {
      const res = await fetch(`${API_BASE}/api/direct/messages/${guestId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ message_type: 'text', message: text }),
      });
      const data = await res.json();

      if (res.ok && data.success && data.data) {
        const msg = data.data;
        const newMsg = {
          id:          msg.id,
          guestId:     msg.guest_id,
          senderId:    msg.sender_id,
          senderName:  userName,
          receiverId:  msg.receiver_id,
          messageType: 'text',
          message:     msg.message,
          fileUrl:     null,
          isRead:      false,
          timestamp:   msg.created_at,
        };

        setMessages(prev =>
          prev.some(m => String(m.id) === String(newMsg.id)) ? prev : [...prev, newMsg]
        );

        sendDirectMessage({
          id:          msg.id,
          guestId:     guestId,
          senderId:    userId,
          senderName:  userName,
          receiverId:  msg.receiver_id,
          messageType: 'text',
          message:     text,
          file_url:    null,
          timestamp:   msg.created_at,
        });
      } else {
        console.error('[DirectChat] Send failed:', data.message);
        alert(data.message || 'Failed to send message.');
        setInputText(text);
      }
    } catch (err) {
      console.error('[DirectChat] Send error:', err);
      alert('Failed to send message. Check your connection.');
      setInputText(text);
    } finally {
      setSending(false);
    }
  }, [inputText, sending, guestId, userId, userName, token]);

  // ── Send file ─────────────────────────────────────────────────────────────
  const handleFileSend = useCallback(async (file, messageType) => {
    if (!file) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message_type', messageType);
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/api/direct/messages/${guestId}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });
      const data = await res.json();

      if (res.ok && data.success && data.data) {
        const msg = data.data;
        const newMsg = {
          id:          msg.id,
          guestId:     msg.guest_id,
          senderId:    msg.sender_id,
          senderName:  userName,
          receiverId:  msg.receiver_id,
          messageType: msg.message_type,
          message:     msg.message || '',
          fileUrl:     msg.file_url ? getProxiedUrl(msg.file_url) : null,
          fileName:    msg.file_name || file.name,
          isRead:      false,
          timestamp:   msg.created_at,
        };
        setMessages(prev =>
          prev.some(m => String(m.id) === String(newMsg.id)) ? prev : [...prev, newMsg]
        );

        sendDirectMessage({
          id:          msg.id,
          guestId:     guestId,
          senderId:    userId,
          senderName:  userName,
          receiverId:  msg.receiver_id,
          messageType: msg.message_type,
          message:     null,
          file_url:    msg.file_url,
          file_name:   msg.file_name || file.name,
          timestamp:   msg.created_at,
        });
      } else {
        alert(data.message || 'Failed to send file.');
      }
    } catch (err) {
      console.error('[DirectChat] File send error:', err);
      alert('Failed to send file. Please try again.');
    } finally {
      setSending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestId, token, userName, userId]);

  // ── Typing ────────────────────────────────────────────────────────────────
  const handleInputChange = useCallback(e => {
    setInputText(e.target.value);
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      emitDirectTypingStart(guestId, userId, userName);
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      emitDirectTypingStop(guestId, userId, userName);
    }, 2000);
  }, [guestId, userId, userName]);

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(msgId => {
    deleteDirectMessage(guestId, msgId, userId);
    setMessages(prev => prev.filter(m => String(m.id) !== String(msgId)));
  }, [guestId, userId]);

  // ── Key press ─────────────────────────────────────────────────────────────
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = ts => {
    try { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const renderMessageContent = (msg) => {
    if (msg.messageType === 'image' && msg.fileUrl) {
      return (
        <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={msg.fileUrl}
            alt="sent"
            className="dc-bubble-image"
            onError={e => { e.target.style.display = 'none'; }}
          />
        </a>
      );
    }
    if ((msg.messageType === 'file' || msg.messageType === 'voice') && msg.fileUrl) {
      return (
        <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" className="dc-bubble-file">
          📎 {msg.fileName || 'Download file'}
        </a>
      );
    }
    return <span className="dc-bubble-text">{msg.message}</span>;
  };

  const title = conversation.title || otherUser?.name || 'Direct Chat';
  const subtitle = conversation.case_type
    ? `${conversation.case_type} Case`
    : otherUser?.name || '';

  return (
    <div className="dc-container">
      {/* ── Header ── */}
      <div className="dc-header">
        <button className="dc-back-btn" onClick={onBack}>‹</button>
        <div className="dc-header-avatar">
          <div className="dc-header-avatar-placeholder">
            {otherUser?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        </div>
        <div className="dc-header-info">
          <p className="dc-header-name">{title}</p>
          <p className="dc-header-sub">
            {connected ? subtitle : 'Connecting…'}
          </p>
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="dc-messages">
        {loadingHistory ? (
          <div className="dc-empty"><p>Loading messages…</p></div>
        ) : messages.length === 0 ? (
          <div className="dc-empty">
            <span>💬</span>
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : null}

        {messages.map(msg => {
          const isMine = String(msg.senderId) === String(userId);
          return (
            <div
              key={msg.id}
              className={`dc-msg-row ${isMine ? 'dc-msg-row--mine' : 'dc-msg-row--theirs'}`}
            >
              {!isMine && (
                <div className="dc-msg-sender-name">{msg.senderName}</div>
              )}
              <div className={`dc-bubble ${isMine ? 'dc-bubble--mine' : 'dc-bubble--theirs'}`}>
                {renderMessageContent(msg)}
                <span className="dc-bubble-time">{formatTime(msg.timestamp)}</span>
                {isMine && (
                  <button className="dc-delete-btn" onClick={() => handleDelete(msg.id)} title="Delete">
                    🗑
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {typingUsers.length > 0 && (
          <div className="dc-typing">
            <span className="dc-typing-dots"><span /><span /><span /></span>
            <p>{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing…</p>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input ── */}
      <div className="dc-input-row">
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files[0];
            if (f) handleFileSend(f, 'image');
            e.target.value = '';
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
          style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files[0];
            if (f) handleFileSend(f, 'file');
            e.target.value = '';
          }}
        />

        <button
          className="dc-media-btn"
          onClick={() => imageInputRef.current?.click()}
          disabled={sending}
          title="Send image"
        >📷</button>

        <button
          className="dc-media-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending}
          title="Send file"
        >📎</button>

        <input
          className="dc-input"
          placeholder={connected ? 'Type a message…' : 'Connecting…'}
          value={inputText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />

        <button
          className="dc-send-btn"
          onClick={handleSend}
          disabled={!inputText.trim() || sending}
        >
          {sending ? '…' : '➤'}
        </button>
      </div>
    </div>
  );
};

export default DirectChat;
